import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { userAPI, clientSettingsAPI, shiftAPI, branchAPI } from '../../../services';
import { calculateProfessionalTax, getPTStateOptions, getPTSlabs, PT_STATES, FREQUENCY_LABELS } from '../../../utils/professionalTax';
import { useAsyncAction } from '../../../hooks';
import {
  Button,
  ErrorMessage,
  SuccessMessage,
  LoadingSpinner,
} from '../../UI';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin, 
  Briefcase,
  Building2,
  Shield,
  DollarSign,
  Gift,
  CreditCard,
  Save,
  CheckCircle,
  XCircle,
  ChevronRight,
  ChevronLeft,
  IndianRupee,
  Calculator,
  FileText,
  Home,
  Heart,
  Banknote,
  Wallet,
  X,
  Plus,
  Info,
  GitBranch
} from 'lucide-react';

const EmployeeFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { user: currentUser } = useAuth();
  // Only users who manage payroll (admin/superadmin, or an employee explicitly granted the
  // "canGeneratePayroll" permission - same gate PF ECR itself uses) can see/edit the PF/ESI/PT
  // statutory section when adding or editing an employee.
  const canManagePF = currentUser?.role === 'admin' || currentUser?.role === 'superadmin' || currentUser?.permissions?.canGeneratePayroll === true;

  // Benefit options for lookup
  const benefitOptions = [
    { id: 'health_insurance', name: 'Health Insurance', amount: 0, frequency: 'monthly' },
    { id: 'life_insurance', name: 'Life Insurance', amount: 0, frequency: 'monthly' },
    { id: 'meal_allowance', name: 'Meal Allowance', amount: 0, frequency: 'monthly' },
    { id: 'transport', name: 'Transport Allowance', amount: 0, frequency: 'monthly' },
    { id: 'mobile', name: 'Mobile Reimbursement', amount: 0, frequency: 'monthly' },
    { id: 'gym', name: 'Gym Membership', amount: 0, frequency: 'monthly' },
    { id: 'education', name: 'Education Allowance', amount: 0, frequency: 'monthly' },
    { id: 'wfh', name: 'Work From Home Allowance', amount: 0, frequency: 'monthly' }
  ];
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]); // Full department objects
  const [allDesignations, setAllDesignations] = useState([]); // All designations with department info
  const [branches, setBranches] = useState([]); // Approval Chain Engine
  const [reportingDropdown, setReportingDropdown] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [autoEmployeeId, setAutoEmployeeId] = useState({ enabled: false, nextEmployeeId: '' });
  
  const { execute: executeCreate } = useAsyncAction();
  const { execute: executeUpdate } = useAsyncAction();
  
  // Client payroll settings (loaded from settings)
  const [clientPayrollSettings, setClientPayrollSettings] = useState({
    basicSalaryPercentage: 50,
    hraPercentage: 40,
    salaryComponents: []
  });
  const [clientEditPermissions, setClientEditPermissions] = useState({
    canEditEmployeeSalary: false,
    canEditPayrollDates: false
  });

  const [form, setForm] = useState({
    // Basic Information
    firstName: '',
    lastName: '',
    employeeId: '',
    dateOfJoining: new Date().toISOString().split('T')[0],
    email: '',
    phone: '',
    gender: '',
    location: '',
    designation: '',
    department: '',
    branchId: '',
    employeeType: 'permanent',
    locationType: 'office', // 'office' = must work from office, 'field' = marketing/sales can work anywhere
    punchFromAnywhere: false, // when true, employee can punch in/out from any location
    role: 'employee',
    permissions: {
      canGeneratePayroll: false,
      canGenerateLetters: false,
      canManageCustomers: false,
      canManageEncashment: false,
      canViewMoodAnalytics: false,
      canHandleSupportTickets: false
    },
    reportingTo: '',
    shift: '',
    probationDays: '',
    username: '',
    password: '',
    sendEmail: true,
    
    // Statutory Components
    statutory: {
      epfEnabled: false,
      esiEnabled: false,
      ptEnabled: false,
      epf: {
        accountNumber: '',
        uan: '',
        contributeToPension: true
      },
      esi: {
        insuranceNumber: ''
      },
      pt: {
        state: ''
      }
    },
    
    // Salary Structure
    salary: {
      annualCTC: 0,
      components: {
        basic: {
          type: 'percentage',
          value: 50,
          monthly: 0,
          annual: 0
        },
        hra: {
          type: 'percentage_of_ctc',
          value: 25,
          monthly: 0,
          annual: 0
        },
        conveyance: {
          type: 'fixed',
          monthly: 0,
          annual: 0,
          enabled: false
        },
        fixedAllowance: {
          type: 'fixed',
          monthly: 0,
          annual: 0
        },
        customAllowances: []
      },
      epfEmployer: {
        enabled: false,
        percentage: 12,
        contributeToPension: false
      }
    },
    
    // Benefits
    benefits: {
      enabled: false,
      items: []
    },
    
    // Personal Details
    personal: {
      dateOfBirth: '',
      age: '',
      fatherName: '',
      pan: '',
      aadhar: '',
      differentlyAbled: '',
      personalEmail: '',
      retirementDate: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        pincode: ''
      },
      emergencyContact: {
        name: '',
        relationship: '',
        phone: ''
      }
    },
    
    // Payment Method
    payment: {
      method: 'bank_transfer',
      bankDetails: {
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: ''
      }
    }
  });

  const steps = [
    { id: 1, label: 'Basic Info', icon: User, description: 'Employee details' },
    { id: 2, label: 'Salary', icon: DollarSign, description: 'CTC & components' },
    { id: 3, label: 'Statutory', icon: Shield, description: 'PF, ESI, PT' },
    { id: 4, label: 'Benefits', icon: Gift, description: 'Optional add-ons' },
    { id: 5, label: 'Personal', icon: Home, description: 'Personal details' },
    { id: 6, label: 'Payment', icon: CreditCard, description: 'Payment method' }
  ];
  // Steps actually shown/reachable - hides Statutory (PF/ESI/PT) from anyone without PF access
  const visibleSteps = steps.filter(s => s.id !== 3 || canManagePF);

  // Filtered designations based on selected department
  const designations = useMemo(() => {
    if (!form.department) return [];
    const selectedDept = departments.find(d => d.name === form.department);
    if (!selectedDept) return allDesignations.map(d => d.name); // fallback to all
    return allDesignations
      .filter(d => d.department?.toString() === selectedDept._id?.toString())
      .map(d => d.name);
  }, [form.department, departments, allDesignations]);

  // Approval Chain Engine: branch selector only matters for branch-wise departments
  const selectedDepartmentObj = useMemo(
    () => departments.find(d => d.name === form.department),
    [form.department, departments]
  );
  const isBranchWiseDept = !!selectedDepartmentObj?.branchWise;

  useEffect(() => {
    fetchLocations();
    fetchReportingDropdown();
    fetchShiftsList();
    fetchBranches();
    if (isEditMode) {
      fetchEmployeeData();
    } else {
      // Fetch next auto-generated employee ID for new employee
      fetchNextEmployeeId();
    }
  }, [id]);

  const fetchReportingDropdown = async () => {
    try {
      const response = await userAPI.getReportingDropdown();
      if (response.status === 'success') {
        setReportingDropdown(response.data?.employees || []);
      }
    } catch (err) {
      console.error('Failed to fetch reporting dropdown:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getBranches();
      if (response.status === 'success') {
        setBranches(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await clientSettingsAPI.getMySettings();
      if (response.status === 'success') {
        setClientEditPermissions({
          canEditEmployeeSalary: response.data?.editPermissions?.canEditEmployeeSalary === true,
          canEditPayrollDates: response.data?.editPermissions?.canEditPayrollDates === true
        });

        // Get office locations from client settings
        const locationsList = response.data?.officeLocations || [];
        setLocations(locationsList);
        
        // Get departments and designations from client settings
        const deptList = (response.data?.departments || []).filter(d => d.isActive !== false);
        const desigList = (response.data?.designations || []).filter(d => d.isActive !== false);
        setDepartments(deptList);
        setAllDesignations(desigList);
        
        // Get payroll settings from client settings
        const payrollSettings = response.data?.payrollSettings || {};
        const basicPercentage = payrollSettings.basicSalaryPercentage ?? 50;
        const hraPercentage = payrollSettings.hraPercentage ?? 40;
        const salaryComponents = payrollSettings.salaryComponents || [];
        
        setClientPayrollSettings({
          basicSalaryPercentage: basicPercentage,
          hraPercentage: hraPercentage,
          salaryComponents: salaryComponents,
          professionalTax: payrollSettings.professionalTax || {}
        });
        
        // Update form with client's payroll settings
        setForm(prev => ({
          ...prev,
          salary: {
            ...prev.salary,
            components: {
              ...prev.salary.components,
              basic: {
                ...prev.salary.components.basic,
                value: basicPercentage
              },
              hra: {
                ...prev.salary.components.hra,
                value: hraPercentage
              }
            }
          }
        }));
      }
    } catch (err) {
      console.error('Failed to fetch office locations:', err);
    }
  };

  const fetchShiftsList = async () => {
    try {
      const res = await shiftAPI.getShifts();
      if (res.status === 'success') {
        setAvailableShifts((res.data || []).filter(s => s.isActive !== false));
      }
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
    }
  };

  const fetchNextEmployeeId = async () => {
    try {
      const res = await clientSettingsAPI.getNextEmployeeId();
      if (res.status === 'success' && res.data && res.data.nextEmployeeId) {
        setAutoEmployeeId({
          enabled: true,
          nextEmployeeId: res.data.nextEmployeeId
        });
        // Always pre-fill the employee ID field with the next auto-generated ID
        setForm(prev => ({
          ...prev,
          employeeId: res.data.nextEmployeeId
        }));
      }
    } catch (err) {
      console.error('Failed to fetch next employee ID:', err);
    }
  };

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getUser(id);
      if (response.status === 'success' && response.data?.user) {
        const user = response.data.user;
        setForm(prev => ({
          ...prev,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          employeeId: user.employeeId || '',
          dateOfJoining: user.dateOfJoining?.split('T')[0] || '',
          email: user.email || '',
          phone: user.phone || '',
          gender: user.gender || '',
          location: user.location?._id || user.location || '',
          designation: user.designation || '',
          department: user.department || '',
          branchId: user.branchId?._id || user.branchId || '',
          employeeType: user.employeeType || 'permanent',
          locationType: user.locationType || 'office',
          punchFromAnywhere: user.punchFromAnywhere || false,
          role: user.role || 'employee',
          permissions: user.permissions || { canGeneratePayroll: false, canGenerateLetters: false, canManageCustomers: false, canManageEncashment: false, canViewMoodAnalytics: false, canHandleSupportTickets: false },
          reportingTo: user.reportingTo?._id || user.reportingTo || '',
          shift: user.shift?._id || user.shift || '',
          probationDays: user.probation?.durationDays || '',
          username: user.username || '',
          statutory: user.statutory || prev.statutory,
          salary: user.salary || prev.salary,
          benefits: user.benefits || prev.benefits,
          personal: {
            ...prev.personal,
            ...user.personal,
            dateOfBirth: user.personal?.dateOfBirth ? new Date(user.personal.dateOfBirth).toISOString().split('T')[0] : '',
            retirementDate: user.personal?.retirementDate ? new Date(user.personal.retirementDate).toISOString().split('T')[0] : ''
          },
          payment: user.payment || prev.payment
        }));
      }
    } catch (err) {
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate salary components based on CTC
  const calculateSalary = useMemo(() => {
    const annualCTC = parseFloat(form.salary.annualCTC) || 0;
    const monthlyCTC = annualCTC / 12;
    
    // Basic calculation (% of CTC) - Use clientPayrollSettings
    const basicPercentage = clientPayrollSettings.basicSalaryPercentage ?? 50;
    const basicAnnual = (annualCTC * basicPercentage) / 100;
    const basicMonthly = basicAnnual / 12;
    
    // HRA calculation (% of Gross/CTC) - Use clientPayrollSettings
    const hraPercentage = clientPayrollSettings.hraPercentage ?? 40;
    const hraAnnual = (annualCTC * hraPercentage) / 100;
    const hraMonthly = hraAnnual / 12;
    
    // Custom Allowances total (user-defined in employee form)
    const customAllowances = form.salary.components.customAllowances || [];
    const customAllowancesMonthly = customAllowances.reduce((sum, a) => sum + (parseFloat(a.monthly) || 0), 0);
    const customAllowancesAnnual = customAllowancesMonthly * 12;
    
    // Admin-defined salary components (from payroll settings)
    const adminComponents = (clientPayrollSettings.salaryComponents || [])
      .filter(comp => comp.isActive !== false)
      .map(comp => {
        let annual = 0;
        if (comp.calculationType === 'percentage_of_ctc') {
          annual = (annualCTC * comp.value) / 100;
        } else if (comp.calculationType === 'percentage_of_basic') {
          annual = (basicAnnual * comp.value) / 100;
        } else if (comp.calculationType === 'fixed') {
          annual = comp.value * 12; // Fixed is monthly amount
        }
        return {
          name: comp.name,
          type: comp.type,
          calculationType: comp.calculationType,
          value: comp.value,
          monthly: annual / 12,
          annual: annual,
          isTaxable: comp.isTaxable
        };
      });
    
    const adminEarningsAnnual = adminComponents.filter(c => c.type === 'earning').reduce((sum, c) => sum + c.annual, 0);
    const adminDeductionsAnnual = adminComponents.filter(c => c.type === 'deduction').reduce((sum, c) => sum + c.annual, 0);
    
    // EPF Employer Contribution (12% of Basic, max ₹15000 per month for PF wage)
    const pfWage = Math.min(basicMonthly, 15000);
    const epfEmployerMonthly = form.salary.epfEmployer?.enabled ? (pfWage * (form.salary.epfEmployer?.percentage || 12) / 100) : 0;
    const epfEmployerAnnual = epfEmployerMonthly * 12;
    
    // Conveyance Allowance is never entered by hand: it is the CTC remaining
    // after Basic and HRA (100% - Basic% - HRA%), less anything else funded from
    // CTC (employer PF, custom and company-defined earnings), so the components
    // always add up to exactly 100% of CTC.
    const conveyancePercentage = Math.max(0, 100 - basicPercentage - hraPercentage);
    const otherCtcFundedAnnual = customAllowancesAnnual + adminEarningsAnnual + epfEmployerAnnual;
    const conveyanceAnnual = Math.max(0, (annualCTC * conveyancePercentage) / 100 - otherCtcFundedAnnual);
    const conveyanceMonthly = conveyanceAnnual / 12;

    // Conveyance now absorbs the remainder, so there is nothing left for the
    // legacy Fixed Allowance; kept at 0 for payroll/payslip compatibility.
    const fixedAllowanceAnnual = 0;
    const fixedAllowanceMonthly = 0;
    
    // The payslip's gross is basic + HRA + conveyance + fixed allowance — the same
    // components the backend assembles into its salary config. PT is slabbed on this,
    // not on CTC (which also carries the employer's PF share).
    const monthlyGross = basicMonthly + hraMonthly + conveyanceMonthly + fixedAllowanceMonthly;

    return {
      monthlyCTC,
      monthlyGross,
      basic: { monthly: basicMonthly, annual: basicAnnual },
      hra: { monthly: hraMonthly, annual: hraAnnual },
      conveyance: { monthly: conveyanceMonthly, annual: conveyanceAnnual, percentage: conveyancePercentage },
      customAllowances: customAllowances.map(a => ({
        ...a,
        monthly: parseFloat(a.monthly) || 0,
        annual: (parseFloat(a.monthly) || 0) * 12
      })),
      customAllowancesTotal: { monthly: customAllowancesMonthly, annual: customAllowancesAnnual },
      adminComponents: adminComponents,
      adminEarnings: { monthly: adminEarningsAnnual / 12, annual: adminEarningsAnnual },
      adminDeductions: { monthly: adminDeductionsAnnual / 12, annual: adminDeductionsAnnual },
      epfEmployer: { monthly: epfEmployerMonthly, annual: epfEmployerAnnual, pfWage },
      fixedAllowance: { monthly: fixedAllowanceMonthly, annual: fixedAllowanceAnnual }
    };
  }, [form.salary.annualCTC, clientPayrollSettings.basicSalaryPercentage, clientPayrollSettings.hraPercentage, clientPayrollSettings.salaryComponents, form.salary.components.customAllowances, form.salary.epfEmployer?.enabled, form.salary.epfEmployer?.percentage]);

  // ── Professional Tax preview ───────────────────────────────────────────────
  const ptStateOptions = useMemo(() => getPTStateOptions(), []);
  const ptClientSettings = clientPayrollSettings.professionalTax || {};
  const ptClientDisabled = ptClientSettings.enabled === false;
  const ptClientOverride = !!ptClientSettings.overrideEmployeeState;

  // Mirrors resolvePTContext() on the backend: a client override wins, then the
  // employee's own state, then the client's default state.
  const ptResolvedState = ptClientOverride
    ? (ptClientSettings.defaultState || '')
    : (form.statutory.pt.state || ptClientSettings.defaultState || '');
  const ptStateConfig = PT_STATES[ptResolvedState] || null;
  const ptUsingClientState = ptResolvedState && ptResolvedState !== form.statutory.pt.state;

  const ptPreview = useMemo(() => calculateProfessionalTax({
    state: ptResolvedState,
    monthlyGross: calculateSalary.monthlyGross,
    gender: form.gender
  }), [ptResolvedState, calculateSalary.monthlyGross, form.gender]);

  const ptFormatRupees = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
  const ptFormatRange = (from, to) => (to === Infinity
    ? `${ptFormatRupees(from)}+`
    : `${ptFormatRupees(from)} – ${ptFormatRupees(to)}`);
  const ptBasisLabel = {
    monthly: 'monthly gross',
    half_yearly: 'half-yearly gross',
    annual: 'annual gross'
  };

  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return '';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Validation
      if (!form.firstName.trim()) {
        setError('First name is required');
        setCurrentStep(1);
        setSaving(false);
        return;
      }

      if (!form.email.trim()) {
        setError('Work email is required');
        setCurrentStep(1);
        setSaving(false);
        return;
      }

      // Prepare data with calculated salary
      // Transform benefits.items from IDs to objects
      const transformedBenefitItems = (form.benefits.items || []).map(itemId => {
        const benefitDef = benefitOptions.find(b => b.id === itemId);
        return benefitDef ? { ...benefitDef } : { id: itemId, name: itemId, amount: 0, frequency: 'monthly' };
      });
      
      // Build probation object if employee type is probation or intern
      const probationData = (form.employeeType === 'probation' || form.employeeType === 'intern') && form.probationDays
        ? { probation: { durationDays: parseInt(form.probationDays) } }
        : {};

      const employeeData = {
        ...form,
        ...probationData,
        benefits: {
          enabled: form.benefits.enabled,
          items: transformedBenefitItems
        },
        salary: {
          annualCTC: form.salary.annualCTC,
          components: {
            basic: {
              type: 'percentage',
              value: form.salary.components.basic.value,
              monthly: calculateSalary.basic.monthly,
              annual: calculateSalary.basic.annual
            },
            hra: {
              type: 'percentage_of_ctc',
              value: form.salary.components.hra?.value || 0,
              monthly: calculateSalary.hra.monthly,
              annual: calculateSalary.hra.annual
            },
            conveyance: {
              type: 'fixed',
              enabled: calculateSalary.conveyance.monthly > 0,
              monthly: calculateSalary.conveyance.monthly,
              annual: calculateSalary.conveyance.annual
            },
            fixedAllowance: {
              type: 'fixed',
              monthly: calculateSalary.fixedAllowance.monthly,
              annual: calculateSalary.fixedAllowance.annual
            },
            customAllowances: (form.salary.components.customAllowances || []).map(a => ({
              name: a.name,
              monthly: parseFloat(a.monthly) || 0,
              annual: (parseFloat(a.monthly) || 0) * 12
            }))
          },
          epfEmployer: {
            enabled: form.salary.epfEmployer?.enabled || false,
            percentage: form.salary.epfEmployer?.percentage || 12,
            contributeToPension: form.salary.epfEmployer?.contributeToPension || false,
            monthly: calculateSalary.epfEmployer.monthly,
            annual: calculateSalary.epfEmployer.annual
          }
        },
        personal: {
          ...form.personal,
          age: calculateAge(form.personal.dateOfBirth)
        }
      };

      // Remove password if empty (for edit mode)
      if (!employeeData.password) {
        delete employeeData.password;
      }
      // Remove temp probationDays field
      delete employeeData.probationDays;
      // Remove sendEmail flag for edit mode - only used on create
      if (isEditMode) {
        delete employeeData.sendEmail;
        if (!clientEditPermissions.canEditEmployeeSalary) {
          delete employeeData.salary;
        }
      }

      let result;
      if (isEditMode) {
        result = await executeUpdate(userAPI.updateUser, id, employeeData);
      } else {
        result = await executeCreate(userAPI.createUser, employeeData);
      }

      if (result.success) {
        // Increment auto employee ID counter if creating new employee with auto-generated ID
        if (!isEditMode && form.employeeId && form.employeeId === autoEmployeeId.nextEmployeeId) {
          try {
            await clientSettingsAPI.incrementEmployeeIdNumber();
          } catch (err) {
            console.error('Failed to increment employee ID:', err);
          }
        }
        setSuccess(isEditMode ? 'Employee updated successfully!' : 'Employee created successfully!');
        setTimeout(() => {
          navigate('/dashboard/employees');
        }, 1500);
      } else {
        setError(result.error || `Failed to ${isEditMode ? 'update' : 'create'} employee`);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const updateForm = (path, value) => {
    setForm(prev => {
      const keys = path.split('.');
      const newForm = { ...prev };
      let current = newForm;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      return newForm;
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      const next = currentStep + 1;
      setCurrentStep(next === 3 && !canManagePF ? 4 : next);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      const prev = currentStep - 1;
      setCurrentStep(prev === 3 && !canManagePF ? 2 : prev);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Header - Fixed at top */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard/employees')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {isEditMode ? 'Edit Employee' : 'Add New Employee'}
                </h1>
                <p className="text-sm text-gray-500">
                  Step {visibleSteps.findIndex(s => s.id === currentStep) + 1} of {visibleSteps.length}: {steps[currentStep - 1].label}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard/employees')}
              >
                Cancel
              </Button>
              {(isEditMode || currentStep === steps.length) && (
                <Button
                  onClick={handleSubmit}
                  loading={saving}
                  className="flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Employee' : 'Create Employee'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps - Fixed below header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between overflow-x-auto scrollbar-hide">
            {visibleSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => setCurrentStep(step.id)}
                    className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all min-w-max ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : isCompleted
                          ? 'text-green-600'
                          : 'text-gray-400'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : isCompleted
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className={`text-sm font-medium ${isActive ? 'text-blue-700' : ''}`}>{step.label}</p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                  </button>
                  {index < visibleSteps.length - 1 && (
                    <div className={`w-8 h-0.5 ${isCompleted ? 'bg-green-400' : 'bg-gray-200'} hidden md:block`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && <ErrorMessage message={error} className="mb-6" />}
        {success && <SuccessMessage message={success} className="mb-6" />}

        <form onSubmit={handleSubmit}>
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
                  <p className="text-sm text-gray-500">Enter employee's work details</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => updateForm('firstName', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="First name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => updateForm('lastName', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Last name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee ID <span className="text-red-500">*</span>
                    {!isEditMode && autoEmployeeId.enabled && (
                      <span className="ml-2 text-xs text-green-600 font-normal">(Auto-generated)</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={form.employeeId}
                    onChange={(e) => updateForm('employeeId', e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="EMP001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Joining <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.dateOfJoining}
                    onChange={(e) => updateForm('dateOfJoining', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateForm('email', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="employee@company.com"
                      required
                    />
                  </div>
                  {!isEditMode && form.email && (
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.sendEmail}
                        onChange={(e) => updateForm('sendEmail', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-600">Send login credentials via email</span>
                    </label>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => updateForm('phone', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="9542643011"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.gender}
                    onChange={(e) => updateForm('gender', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Location <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <select
                      value={form.location}
                      onChange={(e) => updateForm('location', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Location</option>
                      {locations.length === 0 ? (
                        <option disabled>No locations available</option>
                      ) : (
                        locations.filter(loc => loc.isActive !== false).map(loc => (
                          <option key={loc._id || loc.id} value={loc._id || loc.id}>
                            {loc.name}{loc.address ? ` - ${loc.address}` : ''}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  {locations.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600">
                      No office locations found. Please add locations in Settings → Office Locations.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <select
                      value={form.department}
                      onChange={(e) => {
                        const newDept = e.target.value;
                        // Clear designation when department changes
                        setForm(prev => ({
                          ...prev,
                          department: newDept,
                          designation: newDept !== prev.department ? '' : prev.designation
                        }));
                      }}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Department</option>
                      {departments.map(d => (
                        <option key={d._id} value={d.name}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Designation <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <select
                      value={form.designation}
                      onChange={(e) => updateForm('designation', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={!form.department}
                    >
                      <option value="">Select Designation</option>
                      {!form.department ? (
                        <option disabled>Please select a department first</option>
                      ) : designations.length === 0 ? (
                        <option disabled>No designations for this department</option>
                      ) : (
                        designations.map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))
                      )}
                    </select>
                  </div>
                  {form.department && designations.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600">
                      No designations found for this department. Add designations in Settings → Dept & Designations.
                    </p>
                  )}
                </div>

                {isBranchWiseDept && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Branch <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <GitBranch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <select
                        value={form.branchId}
                        onChange={(e) => updateForm('branchId', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required={isBranchWiseDept}
                      >
                        <option value="">Select Branch</option>
                        {branches.map(b => (
                          <option key={b._id} value={b._id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {form.department} repeats its designation ladder per branch - approvals route to this employee's own branch.
                    </p>
                    {branches.length === 0 && (
                      <p className="mt-1 text-xs text-amber-600">
                        No branches found. Add one in Settings → Branches.
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Type
                  </label>
                  <select
                    value={form.employeeType}
                    onChange={(e) => updateForm('employeeType', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="permanent">Permanent</option>
                    <option value="contract">Contract</option>
                    <option value="intern">Intern</option>
                    <option value="probation">Probation</option>
                    <option value="trainee">Trainee</option>
                  </select>
                </div>

                {(form.employeeType === 'probation' || form.employeeType === 'intern') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Probation / Internship Duration (Days)
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="number"
                        value={form.probationDays}
                        onChange={(e) => updateForm('probationDays', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g. 90, 180"
                        min="1"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Number of days for the probation/internship period</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reporting To
                  </label>
                  <select
                    value={form.reportingTo}
                    onChange={(e) => updateForm('reportingTo', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Reporting Manager</option>
                    {reportingDropdown
                      .filter(emp => emp._id !== id)
                      .map(emp => (
                        <option key={emp._id} value={emp._id}>
                          {emp.name} {emp.designation ? `(${emp.designation})` : ''} {emp.employeeId ? `- ${emp.employeeId}` : ''}
                        </option>
                      ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">The person this employee reports to for leave approvals, attendance tracking, etc.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Shift
                  </label>
                  <select
                    value={form.shift}
                    onChange={(e) => updateForm('shift', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Default (Organization Timings)</option>
                    {availableShifts.map(shift => {
                      const formatTime = (t) => {
                        if (!t) return '';
                        const [h, m] = t.split(':').map(Number);
                        const ampm = h >= 12 ? 'PM' : 'AM';
                        return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
                      };
                      return (
                        <option key={shift._id} value={shift._id}>
                          {shift.name} ({formatTime(shift.startTime)} - {formatTime(shift.endTime)})
                          {shift.isNightShift ? ' 🌙' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {form.shift 
                      ? 'Employee will follow the selected shift timings for attendance' 
                      : 'Uses default work timings from organization settings'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Location Type
                  </label>
                  <select
                    value={form.locationType}
                    onChange={(e) => updateForm('locationType', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="office">Office Based</option>
                    <option value="field">Field / Marketing / Sales</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {form.locationType === 'office'
                      ? 'Must punch in/out from assigned office location'
                      : 'Can punch in/out from anywhere (no location restriction)'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Punch From Anywhere
                  </label>
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      form.punchFromAnywhere
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.punchFromAnywhere || false}
                      onChange={(e) => updateForm('punchFromAnywhere', e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-green-600 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Allow punch from any location</span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {form.punchFromAnywhere
                          ? 'Employee can punch in/out from anywhere — office location check is skipped'
                          : 'Office location will be validated on punch in/out'}
                      </p>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={form.role}
                    onChange={(e) => updateForm('role', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="employee">Employee</option>
                    <option value="marketing">Marketing</option>
                  </select>
                </div>

                {/* Special Permissions */}
                {!['admin', 'superadmin'].includes(form.role) && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <Shield className="inline w-4 h-4 mr-1 text-blue-600" />
                      Special Permissions
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          form.permissions?.canGeneratePayroll
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.permissions?.canGeneratePayroll || false}
                          onChange={(e) => updateForm('permissions', { ...form.permissions, canGeneratePayroll: e.target.checked })}
                          className="mt-0.5 w-4 h-4 text-blue-600 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Generate Payroll</span>
                          <p className="text-xs text-gray-500 mt-0.5">Can generate payslips for employees</p>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          form.permissions?.canGenerateLetters
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.permissions?.canGenerateLetters || false}
                          onChange={(e) => updateForm('permissions', { ...form.permissions, canGenerateLetters: e.target.checked })}
                          className="mt-0.5 w-4 h-4 text-blue-600 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Generate Letters</span>
                          <p className="text-xs text-gray-500 mt-0.5">Can generate offer, appointment & experience letters</p>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          form.permissions?.canManageCustomers
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.permissions?.canManageCustomers || false}
                          onChange={(e) => updateForm('permissions', { ...form.permissions, canManageCustomers: e.target.checked })}
                          className="mt-0.5 w-4 h-4 text-blue-600 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Manage Customers</span>
                          <p className="text-xs text-gray-500 mt-0.5">Can add, edit & view customer data</p>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          form.permissions?.canManageEncashment
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.permissions?.canManageEncashment || false}
                          onChange={(e) => updateForm('permissions', { ...form.permissions, canManageEncashment: e.target.checked })}
                          className="mt-0.5 w-4 h-4 text-blue-600 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Manage Leave Encashment</span>
                          <p className="text-xs text-gray-500 mt-0.5">Can configure and approve leave encashment requests</p>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          form.permissions?.canViewMoodAnalytics
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.permissions?.canViewMoodAnalytics || false}
                          onChange={(e) => updateForm('permissions', { ...form.permissions, canViewMoodAnalytics: e.target.checked })}
                          className="mt-0.5 w-4 h-4 text-blue-600 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">View Mood Analytics</span>
                          <p className="text-xs text-gray-500 mt-0.5">Can view the org-wide Mood Analytics dashboard for all employees</p>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          form.permissions?.canHandleSupportTickets
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={form.permissions?.canHandleSupportTickets || false}
                          onChange={(e) => updateForm('permissions', { ...form.permissions, canHandleSupportTickets: e.target.checked })}
                          className="mt-0.5 w-4 h-4 text-blue-600 rounded"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Handle Support Tickets</span>
                          <p className="text-xs text-gray-500 mt-0.5">Can be assigned internal support tickets and resolve them</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Statutory Components - PF/ESI/PT, only visible to users with PF access */}
          {currentStep === 3 && canManagePF && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Statutory Components</h2>
                  <p className="text-sm text-gray-500">Enable the necessary benefits and tax applicable for this employee</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* EPF Toggle */}
                <div className={`p-5 rounded-xl border-2 transition-colors ${form.statutory.epfEnabled ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Employees' Provident Fund</h3>
                        <p className="text-sm text-gray-500">12% employer contribution to PF</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateForm('statutory.epfEnabled', !form.statutory.epfEnabled)}
                      className={`w-14 h-7 rounded-full transition-colors ${form.statutory.epfEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <div className={`w-6 h-6 rounded-full bg-white shadow-sm transform transition-transform ${form.statutory.epfEnabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {form.statutory.epfEnabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">PF Account Number</label>
                        <input
                          type="text"
                          value={form.statutory.epf.accountNumber}
                          onChange={(e) => updateForm('statutory.epf.accountNumber', e.target.value.toUpperCase())}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="AA/AAA/0000000/XXX/0000000"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">UAN</label>
                        <input
                          type="text"
                          value={form.statutory.epf.uan}
                          onChange={(e) => updateForm('statutory.epf.uan', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="000000000000"
                          maxLength={12}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.statutory.epf.contributeToPension}
                            onChange={(e) => updateForm('statutory.epf.contributeToPension', e.target.checked)}
                            className="w-5 h-5 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700">Contribute to Employee Pension Scheme (EPS)</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* ESI Toggle */}
                <div className={`p-5 rounded-xl border-2 transition-colors ${form.statutory.esiEnabled ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <Heart className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Employees' State Insurance</h3>
                        <p className="text-sm text-gray-500">Health insurance for employees</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateForm('statutory.esiEnabled', !form.statutory.esiEnabled)}
                      className={`w-14 h-7 rounded-full transition-colors ${form.statutory.esiEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <div className={`w-6 h-6 rounded-full bg-white shadow-sm transform transition-transform ${form.statutory.esiEnabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {form.statutory.esiEnabled && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-blue-800 font-medium mb-1">ESIC Auto-Calculation</p>
                        <p className="text-sm text-blue-700">
                          Monthly Salary: ₹{calculateSalary.monthlyCTC.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </p>
                        {calculateSalary.monthlyCTC <= 21000 ? (
                          <p className="text-sm text-green-700 font-medium">
                            ESIC Deduction: ₹{Math.round(calculateSalary.monthlyCTC * 0.0075).toLocaleString('en-IN')} (0.75% of salary)
                          </p>
                        ) : (
                          <p className="text-sm text-orange-700 font-medium">
                            ESIC Not Applicable (Salary exceeds ₹21,000)
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">ESI Insurance Number</label>
                        <input
                          type="text"
                          value={form.statutory.esi.insuranceNumber}
                          onChange={(e) => updateForm('statutory.esi.insuranceNumber', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0000000000"
                          maxLength={10}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* PT Toggle */}
                <div className={`p-5 rounded-xl border-2 transition-colors ${form.statutory.ptEnabled ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Professional Tax</h3>
                        <p className="text-sm text-gray-500">State-level tax on employment</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateForm('statutory.ptEnabled', !form.statutory.ptEnabled)}
                      className={`w-14 h-7 rounded-full transition-colors ${form.statutory.ptEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <div className={`w-6 h-6 rounded-full bg-white shadow-sm transform transition-transform ${form.statutory.ptEnabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {form.statutory.ptEnabled && (
                    <div className="pt-4 border-t border-gray-200">
                      {ptClientDisabled ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                          <p className="text-sm text-amber-800">
                            Professional tax is switched off for the whole company in Payroll Settings,
                            so nothing will be deducted for this employee.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              State
                              {ptClientOverride && (
                                <span className="ml-2 text-xs font-normal text-orange-600">
                                  (locked — payroll settings force {PT_STATES[ptClientSettings.defaultState]?.name || 'the company default'})
                                </span>
                              )}
                            </label>
                            <select
                              value={form.statutory.pt.state}
                              onChange={(e) => updateForm('statutory.pt.state', e.target.value)}
                              disabled={ptClientOverride}
                              className={`w-full px-4 py-3 border rounded-lg ${ptClientOverride ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed' : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
                            >
                              <option value="">
                                {ptClientSettings.defaultState
                                  ? `Use company default (${PT_STATES[ptClientSettings.defaultState]?.name || ptStateOptions.find((o) => o.code === ptClientSettings.defaultState)?.name})`
                                  : 'Select State'}
                              </option>
                              <optgroup label="Levies professional tax">
                                {ptStateOptions.filter((o) => o.levied).map((o) => (
                                  <option key={o.code} value={o.code}>
                                    {o.name} ({FREQUENCY_LABELS[o.frequency]})
                                  </option>
                                ))}
                              </optgroup>
                              <optgroup label="No professional tax levied">
                                {ptStateOptions.filter((o) => !o.levied).map((o) => (
                                  <option key={o.code} value={o.code}>{o.name}</option>
                                ))}
                              </optgroup>
                            </select>
                          </div>

                          {!ptResolvedState ? (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                              <p className="text-sm text-amber-800">
                                Pick a state to work out the professional tax slab. No state means no PT deduction.
                              </p>
                            </div>
                          ) : !ptPreview.applicable ? (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-4">
                              <p className="text-sm text-gray-700">{ptPreview.reason}.</p>
                            </div>
                          ) : (
                            <>
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4 mb-4">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-sm text-blue-800 font-medium">
                                    P.Tax — {ptPreview.stateName}
                                  </p>
                                  <span className="text-xs text-blue-600">
                                    Filed {ptPreview.frequencyLabel?.toLowerCase()}
                                  </span>
                                </div>
                                {ptUsingClientState && (
                                  <p className="text-xs text-blue-600 mb-1">
                                    Using the company default state from Payroll Settings.
                                  </p>
                                )}
                                <p className="text-sm text-blue-700">
                                  Monthly Gross: {ptFormatRupees(calculateSalary.monthlyGross)}
                                  {ptPreview.salaryBasis !== 'monthly' && (
                                    <span className="text-blue-600">
                                      {' '}· slab matched on {ptBasisLabel[ptPreview.salaryBasis]} of {ptFormatRupees(ptPreview.evaluatedSalary)}
                                    </span>
                                  )}
                                </p>
                                <p className="text-sm text-green-700 font-medium">
                                  P.Tax Deduction: {ptFormatRupees(ptPreview.baseMonthlyAmount)} / month
                                  <span className="text-blue-600 font-normal ml-1">
                                    ({ptFormatRupees(ptPreview.annualAmount)} per year)
                                  </span>
                                </p>
                                {ptPreview.trueUpMonthName && (
                                  <p className="text-xs text-blue-600 mt-1">
                                    {ptPreview.trueUpMonthName} is {ptFormatRupees(ptPreview.annualAmount - ptPreview.baseMonthlyAmount * 11)} so the twelve months add up to the annual figure.
                                  </p>
                                )}
                              </div>

                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                                <p className="text-xs text-gray-600 font-medium mb-2">
                                  {ptPreview.stateName} slabs
                                  {ptStateConfig?.genderSlabs && (
                                    <span className="font-normal">
                                      {' '}({form.gender === 'female' ? 'female' : 'male'} rates)
                                    </span>
                                  )}
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
                                  {getPTSlabs(ptResolvedState, form.gender).map(([from, to, amount]) => {
                                    const isActive = ptPreview.slab && ptPreview.slab.from === from;
                                    return (
                                      <div
                                        key={`${from}-${to}`}
                                        className={`p-2 rounded border ${isActive ? 'bg-green-50 border-green-300 text-green-800 font-medium' : 'bg-white border-gray-200 text-gray-600'}`}
                                      >
                                        {ptFormatRange(from, to)} → {ptFormatRupees(amount)}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Salary Structure */}
          {currentStep === 2 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
              {isEditMode && !clientEditPermissions.canEditEmployeeSalary && (
                <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Salary changes are locked for this client. Ask super admin to enable salary editing before making revisions.
                </div>
              )}
              {isEditMode && clientEditPermissions.canEditEmployeeSalary && (
                <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                  Super admin approval is active. Salary details can be edited for this employee.
                </div>
              )}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Salary Structure</h2>
                  <p className="text-sm text-gray-500">Set how the employee's salary is divided for accurate pay calculation</p>
                </div>
              </div>

              {/* CTC Input */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Annual CTC <span className="text-red-500">*</span>
                      {isEditMode && !clientEditPermissions.canEditEmployeeSalary && <span className="ml-2 text-xs text-orange-600">(Locked)</span>}
                    </label>
                    <div className="relative">
                      <span className={`absolute left-3 top-1/2 -translate-y-1/2 font-medium ${isEditMode && !clientEditPermissions.canEditEmployeeSalary ? 'text-gray-400' : 'text-gray-500'}`}>₹</span>
                      <input
                        type="number"
                        value={form.salary.annualCTC}
                        onChange={(e) => updateForm('salary.annualCTC', parseFloat(e.target.value) || 0)}
                        className={`w-full pl-8 pr-20 py-4 border rounded-lg text-lg font-semibold ${isEditMode && !clientEditPermissions.canEditEmployeeSalary ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed' : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
                        placeholder="0"
                        min={0}
                        disabled={isEditMode && !clientEditPermissions.canEditEmployeeSalary}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">per year</span>
                    </div>
                    {isEditMode && !clientEditPermissions.canEditEmployeeSalary && (
                      <p className="text-xs text-orange-600 mt-1">CTC cannot be changed after employee is created. Contact super admin for salary revisions.</p>
                    )}
                  </div>
                  <div className="flex items-end">
                    <div className="bg-white rounded-xl p-4 w-full border border-gray-200">
                      <p className="text-sm text-gray-500 mb-1">Monthly CTC</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(calculateSalary.monthlyCTC)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={isEditMode && !clientEditPermissions.canEditEmployeeSalary ? 'pointer-events-none select-none opacity-70' : ''}>

              {/* Salary Components Table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Salary Components</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Calculation Type</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Monthly Amount</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Annual Amount</th>
                      <th className="px-4 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* Earnings Header */}
                    <tr className="bg-green-50">
                      <td colSpan="5" className="px-4 py-2 text-sm font-medium text-green-700">Earnings</td>
                    </tr>
                    
                    {/* Basic */}
                    <tr>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">Basic</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={clientPayrollSettings.basicSalaryPercentage}
                            readOnly
                            disabled
                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 text-sm cursor-not-allowed"
                            min={0}
                            max={100}
                          />
                          <span className="text-sm text-gray-500">% of CTC</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-blue-400 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded-lg z-10">
                              Set in Organization Settings → Payroll
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {formatCurrency(calculateSalary.basic.monthly)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {formatCurrency(calculateSalary.basic.annual)}
                      </td>
                      <td className="px-4 py-3"></td>
                    </tr>
                    
                    {/* House Rent Allowance */}
                    <tr>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">House Rent Allowance</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={clientPayrollSettings.hraPercentage}
                            readOnly
                            disabled
                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 text-sm cursor-not-allowed"
                            min={0}
                            max={100}
                          />
                          <span className="text-sm text-gray-500">% of CTC</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-blue-400 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded-lg z-10">
                              Set in Organization Settings → Payroll
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {formatCurrency(calculateSalary.hra.monthly)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {formatCurrency(calculateSalary.hra.annual)}
                      </td>
                      <td className="px-4 py-3"></td>
                    </tr>
                    
                    {/* Admin-defined Salary Components (Earnings) */}
                    {(calculateSalary.adminComponents || [])
                      .filter(comp => comp.type === 'earning')
                      .map((comp, index) => (
                        <tr key={`admin-earning-${index}`} className="bg-green-25">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{comp.name}</span>
                              <div className="relative group">
                                <Info className="w-4 h-4 text-blue-400 cursor-help" />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded-lg z-10">
                                  Defined in Organization Settings → Payroll
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500">
                                {comp.value}{comp.calculationType === 'fixed' ? ' ₹/mo' : '%'}
                              </span>
                              <span className="text-xs text-gray-400">
                                ({comp.calculationType === 'percentage_of_ctc' ? 'of CTC' : 
                                  comp.calculationType === 'percentage_of_basic' ? 'of Basic' : 'Fixed'})
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-gray-900">
                            {formatCurrency(comp.monthly)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-gray-900">
                            {formatCurrency(comp.annual)}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-400">{comp.isTaxable ? 'Taxable' : 'Non-taxable'}</span>
                          </td>
                        </tr>
                      ))}
                    
                    {/* Conveyance Allowance (auto: 100% - Basic% - HRA%) */}
                    <tr>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">Conveyance Allowance</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={calculateSalary.conveyance.percentage}
                            readOnly
                            disabled
                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 text-sm cursor-not-allowed"
                          />
                          <span className="text-sm text-gray-500">% of CTC</span>
                          <div className="relative group">
                            <Info className="w-4 h-4 text-blue-400 cursor-help" />
                            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-56 p-2 bg-gray-800 text-white text-xs rounded-lg z-10">
                              Auto-calculated: 100% − Basic % − HRA %, less employer PF and any other allowances
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {formatCurrency(calculateSalary.conveyance.monthly)}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">
                        {formatCurrency(calculateSalary.conveyance.annual)}
                      </td>
                      <td className="px-4 py-3"></td>
                    </tr>
                    
                    {/* Custom Allowances */}
                    {(form.salary.components.customAllowances || []).map((allowance, index) => (
                      <tr key={`custom-${index}`}>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={allowance.name || ''}
                            onChange={(e) => {
                              const updated = [...(form.salary.components.customAllowances || [])];
                              updated[index] = { ...updated[index], name: e.target.value };
                              updateForm('salary.components.customAllowances', updated);
                            }}
                            placeholder="Allowance Name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-500">Fixed amount</span>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={allowance.monthly || 0}
                            onChange={(e) => {
                              const updated = [...(form.salary.components.customAllowances || [])];
                              updated[index] = { ...updated[index], monthly: parseFloat(e.target.value) || 0 };
                              updateForm('salary.components.customAllowances', updated);
                            }}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm text-right"
                            min={0}
                          />
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {formatCurrency((allowance.monthly || 0) * 12)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (form.salary.components.customAllowances || []).filter((_, i) => i !== index);
                              updateForm('salary.components.customAllowances', updated);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    
                    {/* Add Allowance Button */}
                    <tr>
                      <td colSpan="5" className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const current = form.salary.components.customAllowances || [];
                              updateForm('salary.components.customAllowances', [...current, { name: '', monthly: 0 }]);
                            }}
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                          >
                            <Plus className="w-4 h-4" />
                            Add Custom Allowance
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Admin-defined Salary Components (Deductions) */}
                    {(calculateSalary.adminComponents || []).filter(comp => comp.type === 'deduction').length > 0 && (
                      <>
                        <tr className="bg-red-50">
                          <td colSpan="5" className="px-4 py-2 text-sm font-medium text-red-700">Deductions (Company Defined)</td>
                        </tr>
                        {(calculateSalary.adminComponents || [])
                          .filter(comp => comp.type === 'deduction')
                          .map((comp, index) => (
                            <tr key={`admin-deduction-${index}`}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{comp.name}</span>
                                  <div className="relative group">
                                    <Info className="w-4 h-4 text-red-400 cursor-help" />
                                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded-lg z-10">
                                      Defined in Organization Settings → Payroll
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-500">
                                    {comp.value}{comp.calculationType === 'fixed' ? ' ₹/mo' : '%'}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    ({comp.calculationType === 'percentage_of_ctc' ? 'of CTC' : 
                                      comp.calculationType === 'percentage_of_basic' ? 'of Basic' : 'Fixed'})
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-red-600">
                                -{formatCurrency(comp.monthly)}
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-red-600">
                                -{formatCurrency(comp.annual)}
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs text-gray-400">{comp.isTaxable ? 'Taxable' : 'Non-taxable'}</span>
                              </td>
                            </tr>
                          ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Benefits Section - EPF */}
              <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
                <div className="bg-purple-50 px-4 py-2">
                  <span className="text-sm font-medium text-purple-700">Benefits</span>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium text-gray-900">EPF - Employer Contribution</h4>
                      <p className="text-sm text-gray-500">Employee Contribution</p>
                      <p className="text-xs text-blue-600">12% of Actual PF Wage</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => updateForm('salary.epfEmployer.enabled', !form.salary.epfEmployer?.enabled)}
                      className={`w-14 h-7 rounded-full transition-colors ${form.salary.epfEmployer?.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <div className={`w-6 h-6 rounded-full bg-white shadow-sm transform transition-transform ${form.salary.epfEmployer?.enabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  
                  {form.salary.epfEmployer?.enabled && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Contribution Rate:</span>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={form.salary.epfEmployer?.percentage || 12}
                            onChange={(e) => updateForm('salary.epfEmployer.percentage', parseFloat(e.target.value) || 12)}
                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm text-right"
                            min={0}
                            max={100}
                            step={0.01}
                          />
                          <span className="text-sm text-gray-500">% of PF Wages</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">PF Wage (capped at ₹15,000):</span>
                        <span className="font-medium text-gray-900">{formatCurrency(calculateSalary.epfEmployer.pfWage)}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Monthly Employer Contribution:</span>
                        <span className="font-medium text-green-600">{formatCurrency(calculateSalary.epfEmployer.monthly)}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Annual Employer Contribution:</span>
                        <span className="font-medium text-green-600">{formatCurrency(calculateSalary.epfEmployer.annual)}</span>
                      </div>
                      
                      <div className="pt-3 border-t border-gray-200">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.salary.epfEmployer?.contributeToPension || false}
                            onChange={(e) => updateForm('salary.epfEmployer.contributeToPension', e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Contribute to Employee Pension Scheme</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Cost to Company Summary */}
              <div className="bg-blue-50 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Cost to Company</span>
                  <div className="flex gap-8">
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Monthly</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(calculateSalary.monthlyCTC)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Annual</p>
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(form.salary.annualCTC)}</p>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
          )}

          {/* Step 4: Benefits */}
          {currentStep === 4 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Other Benefits</h2>
                  <p className="text-sm text-gray-500">Optional add-ons to employee salary structure</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">Do you offer Benefits for this employee?</h3>
                    <p className="text-sm text-gray-500">Add Benefits and define contribution details for this employee</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => updateForm('benefits.enabled', !form.benefits.enabled)}
                    className={`w-14 h-7 rounded-full transition-colors ${form.benefits.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <div className={`w-6 h-6 rounded-full bg-white shadow-sm transform transition-transform ${form.benefits.enabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>

              {form.benefits.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { id: 'health_insurance', label: 'Health Insurance', description: 'Medical coverage for employee and family' },
                    { id: 'life_insurance', label: 'Life Insurance', description: 'Term life insurance coverage' },
                    { id: 'meal_allowance', label: 'Meal Allowance', description: 'Daily meal allowance benefit' },
                    { id: 'transport', label: 'Transport Allowance', description: 'Commute expense reimbursement' },
                    { id: 'mobile', label: 'Mobile Reimbursement', description: 'Monthly mobile bill reimbursement' },
                    { id: 'gym', label: 'Gym Membership', description: 'Fitness center membership' },
                    { id: 'education', label: 'Education Allowance', description: 'Learning and development fund' },
                    { id: 'wfh', label: 'Work From Home Allowance', description: 'Home office setup and expenses' }
                  ].map(benefit => {
                    const isSelected = form.benefits.items?.includes(benefit.id);
                    return (
                      <div
                        key={benefit.id}
                        onClick={() => {
                          const items = form.benefits.items || [];
                          const newItems = isSelected 
                            ? items.filter(i => i !== benefit.id)
                            : [...items, benefit.id];
                          updateForm('benefits.items', newItems);
                        }}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{benefit.label}</h4>
                            <p className="text-sm text-gray-500">{benefit.description}</p>
                          </div>
                          {isSelected ? (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          ) : (
                            <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!form.benefits.enabled && (
                <div className="text-center py-12 text-gray-500">
                  <Gift className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Enable benefits above to add optional add-ons for this employee</p>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Personal Details */}
          {currentStep === 5 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Home className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Personal Details</h2>
                  <p className="text-sm text-gray-500">Employee's personal and contact information</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.personal.dateOfBirth}
                    onChange={(e) => updateForm('personal.dateOfBirth', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Retirement Date</label>
                  <input
                    type="date"
                    value={form.personal.retirementDate}
                    onChange={(e) => updateForm('personal.retirementDate', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Drives the automatic retirement leave-encashment trigger</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
                  <input
                    type="text"
                    value={calculateAge(form.personal.dateOfBirth) || ''}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                    disabled
                    placeholder="Auto-calculated"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father's Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.personal.fatherName}
                    onChange={(e) => updateForm('personal.fatherName', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Father's name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">PAN</label>
                  <input
                    type="text"
                    value={form.personal.pan}
                    onChange={(e) => updateForm('personal.pan', e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="AAAAA0000A"
                    maxLength={10}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Aadhar Number</label>
                  <input
                    type="text"
                    value={form.personal.aadhar}
                    onChange={(e) => {
                      // Only allow numbers and format as XXXX XXXX XXXX
                      const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                      updateForm('personal.aadhar', value);
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="123412341234"
                    maxLength={12}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Differently Abled Type</label>
                  <select
                    value={form.personal.differentlyAbled}
                    onChange={(e) => updateForm('personal.differentlyAbled', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Not Applicable</option>
                    <option value="visual">Visual Impairment</option>
                    <option value="hearing">Hearing Impairment</option>
                    <option value="locomotor">Locomotor Disability</option>
                    <option value="intellectual">Intellectual Disability</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Personal Email Address</label>
                  <input
                    type="email"
                    value={form.personal.personalEmail}
                    onChange={(e) => updateForm('personal.personalEmail', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="personal@email.com"
                  />
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Residential Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1</label>
                    <input
                      type="text"
                      value={form.personal.address.line1}
                      onChange={(e) => updateForm('personal.address.line1', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="House/Flat No., Building Name"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2</label>
                    <input
                      type="text"
                      value={form.personal.address.line2}
                      onChange={(e) => updateForm('personal.address.line2', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Street, Area, Landmark"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      value={form.personal.address.city}
                      onChange={(e) => updateForm('personal.address.city', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                    <select
                      value={form.personal.address.state}
                      onChange={(e) => updateForm('personal.address.state', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select State</option>
                      <option value="Andhra Pradesh">Andhra Pradesh</option>
                      <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                      <option value="Assam">Assam</option>
                      <option value="Bihar">Bihar</option>
                      <option value="Chhattisgarh">Chhattisgarh</option>
                      <option value="Goa">Goa</option>
                      <option value="Gujarat">Gujarat</option>
                      <option value="Haryana">Haryana</option>
                      <option value="Himachal Pradesh">Himachal Pradesh</option>
                      <option value="Jharkhand">Jharkhand</option>
                      <option value="Karnataka">Karnataka</option>
                      <option value="Kerala">Kerala</option>
                      <option value="Madhya Pradesh">Madhya Pradesh</option>
                      <option value="Maharashtra">Maharashtra</option>
                      <option value="Manipur">Manipur</option>
                      <option value="Meghalaya">Meghalaya</option>
                      <option value="Mizoram">Mizoram</option>
                      <option value="Nagaland">Nagaland</option>
                      <option value="Odisha">Odisha</option>
                      <option value="Punjab">Punjab</option>
                      <option value="Rajasthan">Rajasthan</option>
                      <option value="Sikkim">Sikkim</option>
                      <option value="Tamil Nadu">Tamil Nadu</option>
                      <option value="Telangana">Telangana</option>
                      <option value="Tripura">Tripura</option>
                      <option value="Uttar Pradesh">Uttar Pradesh</option>
                      <option value="Uttarakhand">Uttarakhand</option>
                      <option value="West Bengal">West Bengal</option>
                      <option value="Delhi">Delhi</option>
                      <option value="Jammu & Kashmir">Jammu &amp; Kashmir</option>
                      <option value="Ladakh">Ladakh</option>
                      <option value="Chandigarh">Chandigarh</option>
                      <option value="Puducherry">Puducherry</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PIN Code</label>
                    <input
                      type="text"
                      value={form.personal.address.pincode}
                      onChange={(e) => updateForm('personal.address.pincode', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="000000"
                      maxLength={6}
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact Section */}
              <div className="mt-8">
                <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Emergency Contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
                    <input
                      type="text"
                      value={form.personal.emergencyContact?.name || ''}
                      onChange={(e) => updateForm('personal.emergencyContact.name', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Emergency contact name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                    <select
                      value={form.personal.emergencyContact?.relationship || ''}
                      onChange={(e) => updateForm('personal.emergencyContact.relationship', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select relationship</option>
                      <option value="Father">Father</option>
                      <option value="Mother">Mother</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Brother">Brother</option>
                      <option value="Sister">Sister</option>
                      <option value="Friend">Friend</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={form.personal.emergencyContact?.phone || ''}
                      onChange={(e) => updateForm('personal.emergencyContact.phone', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="10-digit phone number"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Payment Method */}
          {currentStep === 6 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Payment Method</h2>
                  <p className="text-sm text-gray-500">How would you like to pay this employee?</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {[
                  { id: 'direct_deposit', label: 'Direct Deposit', description: 'Automated salary transfer', icon: Banknote, highlight: true },
                  { id: 'bank_transfer', label: 'Bank Transfer', description: 'Manual bank process', icon: CreditCard },
                  { id: 'cheque', label: 'Cheque', description: 'Pay by cheque', icon: FileText },
                  { id: 'cash', label: 'Cash', description: 'Pay in cash', icon: Wallet }
                ].map(method => {
                  const Icon = method.icon;
                  const isSelected = form.payment.method === method.id;
                  return (
                    <div
                      key={method.id}
                      onClick={() => updateForm('payment.method', method.id)}
                      className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-50' 
                          : method.highlight 
                            ? 'border-green-200 bg-green-50 hover:border-green-300' 
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{method.label}</h4>
                          <p className="text-sm text-gray-500">{method.description}</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {(form.payment.method === 'direct_deposit' || form.payment.method === 'bank_transfer') && (
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Bank Account Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                      <input
                        type="text"
                        value={form.payment.bankDetails.accountNumber}
                        onChange={(e) => updateForm('payment.bankDetails.accountNumber', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter account number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                      <input
                        type="text"
                        value={form.payment.bankDetails.ifscCode}
                        onChange={(e) => updateForm('payment.bankDetails.ifscCode', e.target.value.toUpperCase())}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="ABCD0001234"
                        maxLength={11}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                      <input
                        type="text"
                        value={form.payment.bankDetails.bankName}
                        onChange={(e) => updateForm('payment.bankDetails.bankName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Bank name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Branch Name</label>
                      <input
                        type="text"
                        value={form.payment.bankDetails.branchName}
                        onChange={(e) => updateForm('payment.bankDetails.branchName', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Branch name"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>

            <div className="flex items-center gap-3">
              {currentStep < steps.length ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  loading={saving}
                  className="flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update Employee' : 'Create Employee'}
                </Button>
              )}
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFormPage;
